import { cva, type VariantProps } from 'class-variance-authority';

import { DARK_CONTROL } from '../../styles/elevation';

/**
 * Input visual variants — mirror the Figma "Flashka Design System" file
 * `YZK9r3wDhvw1pY60KlVEIj`, frame `3369:15852` ("Input text field", light) /
 * `4225:13872` (dark). Reference cells (Size M, Field with label + helper):
 *   - 3368:6293 – Default        (border secondary-300, placeholder secondary-400)
 *   - 3368:6353 – Hover          (border deepens to secondary-400)
 *   - 3368:5592 – Focused        (outer 1px secondary-600 ring wrapper + p-px,
 *                                 radius +1; placeholder shifts to secondary-500)
 *   - 3368:6248 – Active typing  (border secondary-800, value secondary-800)
 *   - 3368:6324 – Filled         (border secondary-300, value secondary-800)
 *   - 3368:5732 – Disabled       (whole field group at opacity-50)
 *   - 3368:6223 – Error          (border + helper red-600)
 *   - 3368:5804 – Success        (border + helper green-600)
 * Sizes (Basic/Default cells 3368:5738 / 6375 / 5669 / 5279 / 5093):
 *   s 36px r16 · m 40px r18 · l 44px r18 · xl 48px r20 · xxl 52px r20
 *   (Figma names them S/M/L/XL/XLL; "XLL" maps to our standard `xxl`.)
 *   Text is B2 (14/22) up to l, B1 (16/24) from xl. Label + helper are B3
 *   (12/20) at every size; label row is a fixed 24px tall; rows sit 4px apart.
 * Slots (M cells 3368:6320 / 6092 / 6122 / 3372:1155 / 3368:6351):
 *   left icon 18px secondary-500, pl-10 gap-6 · right icon pr-10 gap-4 ·
 *   prefix/suffix text secondary-500 with a full-height secondary-300
 *   divider (gap-8 left / gap-10 right) · right button slot pr-7 gap-4.
 *
 * Focus anatomy — Figma's Focused cell shows a 1px `secondary-600` outline
 * at radius+1 hugging the field. We render it as an absolute overlay 1px
 * OUTSIDE the field (`-inset-px`, see `inputFocusRingVariants`) so it sits
 * flush against the field border with no gap AND without reflowing the
 * field's content (the earlier border+padding wrapper pushed the text in 2px
 * on focus — a visible shift). The field keeps a constant size; only the ring
 * overlay appears while focused-and-empty. Once the user types, the ring
 * drops and the field border darkens to secondary-800 ("Active typing"); an
 * unfocused non-empty field is "Filled".
 *
 * Dark mode is automatic: every token resolves through the auto-inverting
 * CSS-var palette — the dark Figma nodes reference the *same* variables
 * (`secondary-*`, `red-600`, `green-600`, the white→black `secondary/30`
 * surface), verified via get_variable_defs on 4225:16080/16697/16759. No
 * `dark:` classes needed. The lone hex hand-off is the native placeholder /
 * icon color (props, not classes) — resolved in `Input.icons.ts`.
 */

/** Root column: label row + ring wrapper + helper row, 4px apart. */
/**
 * The field label — Figma "Field (with label)": B3 medium, `secondary-600`.
 * Exported so screens labelling OTHER field-like controls (a
 * `DropdownSelector` chip, a language picker) use the exact input-label type
 * instead of hand-rolling one.
 */
export const inputLabelClass =
  'font-medium text-b3 text-secondary-600 native:font-mona-medium';

export const inputRootVariants = cva('flex w-full flex-col items-start gap-1', {
  variants: {
    // Figma disabled cell 3368:5732 dims the whole group (label + field +
    // helper) to 50% — unlike Button's !opacity-30, which is button-only spec.
    disabled: { true: 'opacity-50', false: '' },
  },
  defaultVariants: { disabled: false },
});

/**
 * The field host — owns the field height and anchors the absolute focus-ring
 * overlay. `relative` so the overlay positions against it. The field inside
 * is `h-full`, so the field stays a constant size whether or not the ring is
 * showing (no reflow on focus).
 */
export const inputRingVariants = cva('relative w-full', {
  variants: {
    size: {
      s: 'h-9',
      m: 'h-10',
      l: 'h-11',
      xl: 'h-12',
      xxl: 'h-[52px]',
    },
  },
  defaultVariants: { size: 'm' },
});

/**
 * The focus ring (Figma "Focused"): a 1px secondary-600 outline drawn as an
 * absolute overlay 1px OUTSIDE the field (`-inset-px`) so it sits FLUSH
 * against the field's border — its inner edge meets the field's outer edge,
 * no gap — without shifting the field's content or changing its size. Radius
 * is field-radius + 1 to stay concentric. Rendered only while
 * focused-and-empty; non-interactive (`web:pointer-events-none` + native
 * `pointerEvents`) so taps pass through to the field.
 */
export const inputFocusRingVariants = cva(
  'web:pointer-events-none absolute -inset-px border border-secondary-600',
  {
    variants: {
      size: {
        s: 'rounded-[17px]',
        m: 'rounded-[19px]',
        l: 'rounded-[19px]',
        xl: 'rounded-[21px]',
        xxl: 'rounded-[21px]',
      },
    },
    defaultVariants: { size: 'm' },
  },
);

/**
 * The bordered field surface the text + slots live in.
 *
 * Dark: a field is a CONTROL — one clear step above the surface it sits on
 * (elevation.ts), never the mirrored `secondary-50/70` hole it used to be
 * (design review 2026-10). Light keeps the Figma white glass.
 */
export const inputFieldVariants = cva(
  'flex h-full w-full flex-row items-center overflow-hidden border bg-secondary-50/70 ' +
    `${DARK_CONTROL} ` +
    'web:transition-colors web:duration-150',
  {
    variants: {
      size: {
        s: 'rounded-[16px]',
        m: 'rounded-[18px]',
        l: 'rounded-[18px]',
        xl: 'rounded-[20px]',
        xxl: 'rounded-[20px]',
      },
      /**
       * rest    → Default/Filled (border 300, hover 400 — web pointer only)
       * focused → Focused (border stays 300; the overlay ring paints the focus)
       * typing  → Active typing (border darkens to 800)
       */
      state: {
        // Dark rest border: `secondary-300`, a quiet hairline the control fill
        // backs up (owner, 2026-10: s400 read too light); hover steps to s400.
        rest: 'border-secondary-300 dark:border-secondary-300 web:hover:border-secondary-400 dark:web:hover:border-secondary-400',
        focused: 'border-secondary-300 dark:border-secondary-400',
        typing: 'border-secondary-800',
      },
      /** Error/Success recolor the border regardless of interaction state. */
      status: {
        none: '',
        error: '!border-red-600',
        success: '!border-green-600',
      },
      // Horizontal padding per slot occupancy (Figma: 12px text-only, 10px
      // next to an icon, 7px next to the 28px button slot).
      leftInset: { text: 'pl-3', icon: 'pl-2.5' },
      rightInset: { text: 'pr-3', icon: 'pr-2.5', slot: 'pr-[7px]' },
    },
    defaultVariants: {
      size: 'm',
      state: 'rest',
      status: 'none',
      leftInset: 'text',
      rightInset: 'text',
    },
  },
);

/**
 * The text element itself (shared between the real input and prefix/suffix
 * sizing). B2 (14/22) through l, B1 (16/24) from xl; Mona Sans Medium. The
 * typed value is secondary-800 in every state (Filled/Active typing/Error/
 * Success cells); placeholder color is handled separately (web `placeholder:`
 * classes on the input, hex prop on native).
 *
 * Native gets fontSize-ONLY classes (same rem values as text-b2/b1, so the
 * responsive-rem scaling still applies): a single-line RN TextInput on iOS
 * draws the typed value low in its line box when an explicit lineHeight
 * (text-b2/b1 bundle one) exceeds the font's natural line, while the
 * placeholder ignores it — so content visibly shifted down on the first
 * keystroke. No lineHeight = value + placeholder share font metrics. The
 * fixed field height (ring h-*) owns the vertical rhythm, so the missing
 * lineHeight changes nothing else on native; web keeps the full text-b2/b1.
 */
export const inputTextVariants = cva(
  'w-full min-w-0 flex-1 font-medium text-secondary-800 native:font-mona-medium ' +
    'web:caret-secondary-800 ' +
    // Chrome/Safari paint an autofilled value on their own pale-blue ground,
    // which no `background` rule can override — only an inset box-shadow in
    // the field's colour (and the text-fill colour) keeps the DS look after
    // the user picks a browser suggestion (seen on onboarding's name field).
    'web:autofill:shadow-[inset_0_0_0_1000px_theme(colors.secondary.50)] ' +
    'web:autofill:[-webkit-text-fill-color:theme(colors.secondary.800)]',
  {
    variants: {
      size: {
        s: 'web:text-b2 native:text-[0.875rem]',
        m: 'web:text-b2 native:text-[0.875rem]',
        l: 'web:text-b2 native:text-[0.875rem]',
        xl: 'web:text-b1 native:text-[1rem]',
        xxl: 'web:text-b1 native:text-[1rem]',
      },
      /** Placeholder: secondary-400 at rest, secondary-500 while focused (web). */
      focused: {
        true: 'web:placeholder:text-fg-subtle',
        false: 'web:placeholder:text-secondary-400',
      },
      /** Horizontal text alignment — `center` for short numeric count fields. */
      align: { left: 'text-left', center: 'text-center' },
    },
    defaultVariants: { size: 'm', focused: false, align: 'left' },
  },
);

/** Prefix/suffix affix text (e.g. "https://", "@gmail.com") — secondary-500. */
export const inputAffixTextVariants = cva(
  'shrink-0 whitespace-nowrap font-medium text-fg-subtle native:font-mona-medium',
  {
    variants: {
      size: {
        s: 'text-b2',
        m: 'text-b2',
        l: 'text-b2',
        xl: 'text-b1',
        xxl: 'text-b1',
      },
    },
    defaultVariants: { size: 'm' },
  },
);

export type InputRootVariantProps = VariantProps<typeof inputRootVariants>;
export type InputRingVariantProps = VariantProps<typeof inputRingVariants>;
export type InputFieldVariantProps = VariantProps<typeof inputFieldVariants>;
export type InputTextVariantProps = VariantProps<typeof inputTextVariants>;
